import { DetailsHeader } from "@components/backoffice/DetailsHeader";
import { PatronIcon } from "@components/backoffice/icons";
import { CopyButton } from "@components/CopyButton";
import { EmailLink } from "@components/EmailLink";
import { invenioConfig } from "@config";
import { getUserImageInfo } from "@api/profile/profile";
import { userApi } from "@api/users/user";
import FacultyLibraryIdSection from "@authentication/pages/Profile/ProfileForm/FacultyLibraryIdSection";
import PropTypes from "prop-types";
import React, { Component } from "react";
import {
  Header,
  Image,
  Placeholder,
  Grid,
  Icon,
  Modal,
  Message,
  Loader,
} from "semantic-ui-react";
import LibraryCard from "@authentication/pages/Profile/ProfileForm/LibraryCard";

const FALLBACK_MESSAGES = {
  NO_PROFILE_DATA:
    "Using available patron data only - extended profile data not available",
  PROFILE_LOAD_ERROR: "Some profile information could not be loaded",
  NO_USER_ID: "No user ID available",
};

const FIELD_LABELS = {
  email: { label: "Email Address", icon: "mail" },
  username: { label: "Username", icon: "user" },
  full_name: { label: "Full Name", icon: "user outline" },
  contact_number: { label: "Contact Number", icon: "phone" },
  department: { label: "Department", icon: "building" },
  program: { label: "Program", icon: "book" },
  year: { label: "Year Level", icon: "calendar" },
  semester: { label: "Semester", icon: "calendar alternate" },
};

const STYLES = {
  profileImageContainer: (size) => ({
    width: size,
    height: size,
    marginRight: size === 120 ? "" : "1rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "right",
    backgroundColor: "transparent",
    borderRadius: "50%",
    border: "2px solid #f4b952",
    boxShadow: size === 120 ? "0 2px 8px rgba(0,0,0,0.1)" : "none",
  }),
  sectionTitle: {
    fontSize: "1.3em",
    fontWeight: "bold",
    color: "#f4b952",
    marginBottom: "1rem",
    borderBottom: "2px solid #f4b952",
    paddingBottom: "0.5rem",
  },
  fieldLabel: {
    fontWeight: "bold",
    color: "#666",
    marginBottom: "0.5rem",
    fontSize: "14px",
  },
  fieldValue: (hasValue) => ({
    color: hasValue ? "#333" : "#999",
    fontStyle: hasValue ? "normal" : "italic",
    fontSize: "0.95em",
  }),
  libraryCardContainer: {
    marginBottom: "2rem",
    padding: "1rem",
    backgroundColor: "transparent",
    borderRadius: "8px",
    border: "1px solid #e9ecef",
  },
};

export default class PatronHeader extends Component {
  constructor(props) {
    super(props);
    this.state = {
      profileImage: null,
      imageLoading: true,
      imageError: false,
      showModal: false,
      profileData: null,
      profileLoading: false,
      profileError: null,
    };
    // Add mounted flag to track component lifecycle
    this._isMounted = false;
  }

  async componentDidMount() {
    this._isMounted = true;
    this.loadData();
  }

  async componentDidUpdate(prevProps) {
    const { data } = this.props;
    const { data: prevData } = prevProps;
    if (prevData.metadata.id !== data.metadata.id) {
      this.loadData();
    }
  }

  componentWillUnmount() {
    // Set mounted flag to false when component unmounts
    this._isMounted = false;
  }

  // Helper method to safely update state
  safeSetState = (stateUpdate) => {
    if (this._isMounted) {
      this.setState(stateUpdate);
    }
  };

  loadData = async () => {
    await Promise.all([this.loadProfileImage(), this.loadProfileData()]);
  };

  loadProfileImage = async () => {
    const { data } = this.props;
    const userId = data.metadata.id;
    if (!userId) {
      this.safeSetState({ imageLoading: false, imageError: true });
      return;
    }

    this.safeSetState({ imageLoading: true, imageError: false });

    try {
      const imageInfo = await getUserImageInfo(userId);
      this.safeSetState({
        profileImage: imageInfo?.image_url,
        imageLoading: false,
        imageError: !imageInfo?.has_image,
      });
    } catch (error) {
      this.safeSetState({
        profileImage: null,
        imageLoading: false,
        imageError: true,
      });
    }
  };

  createFallbackData = (metadata) => ({
    id: metadata.id,
    email: metadata.email,
    name: metadata.name,
    full_name: metadata.name,
    username: metadata.email?.split("@")[0] || `user_${metadata.id}`,
    pid: metadata.pid,
    profile_id: metadata.profile_id,
    location_pid: metadata.location_pid,
    role: metadata.role,
    department: null,
    program: null,
    year: null,
    semester: null,
    contact_number: null,
    library_id: null,
  });

  loadProfileData = async () => {
    const { data } = this.props;
    const userId = data.metadata.id;

    if (!userId) {
      this.safeSetState({
        profileLoading: false,
        profileError: FALLBACK_MESSAGES.NO_USER_ID,
      });
      return;
    }

    this.safeSetState({ profileLoading: true, profileError: null });

    try {
      const profileResponse = await userApi.getUserProfileById(userId);
      if (profileResponse?.data) {
        this.safeSetState({
          profileData: profileResponse.data,
          profileLoading: false,
          profileError: null,
        });
        return;
      }

      // Fallback to patron metadata
      const enhancedData = this.createFallbackData(data.metadata);
      this.safeSetState({
        profileData: enhancedData,
        profileLoading: false,
        profileError: FALLBACK_MESSAGES.NO_PROFILE_DATA,
      });
    } catch (error) {
      // Handle API errors
      const enhancedData = this.createFallbackData(data.metadata);
      this.safeSetState({
        profileData: enhancedData,
        profileLoading: false,
        profileError: FALLBACK_MESSAGES.PROFILE_LOAD_ERROR,
      });
    }
  };

  openModal = () => this.safeSetState({ showModal: true });
  closeModal = () => this.safeSetState({ showModal: false });

  renderProfileImage = (size = "compact") => {
    const { profileImage, imageLoading, imageError } = this.state;
    const { data } = this.props;

    const imageSize = size === "large" ? 120 : 60;

    if (imageLoading) {
      return (
        <Placeholder style={STYLES.profileImageContainer(imageSize)}>
          <Placeholder.Image square />
        </Placeholder>
      );
    }

    if (profileImage && !imageError) {
      return (
        <Image
          src={profileImage}
          size={size === "large" ? "small" : "tiny"}
          circular
          style={{
            ...STYLES.profileImageContainer(imageSize),
            objectFit: "cover",
          }}
          alt={`${data.metadata.name}'s profile picture`}
          onError={() =>
            this.safeSetState({ imageError: true, profileImage: null })
          }
        />
      );
    }

    return (
      <div style={STYLES.profileImageContainer(imageSize)}>
        <PatronIcon size={size === "large" ? "huge" : "large"} />
      </div>
    );
  };

  renderField = (key, value) => {
    const config = FIELD_LABELS[key];
    if (!config) return null;

    return (
      <div style={{ marginBottom: "1rem" }}>
        <div style={STYLES.fieldLabel}>
          <Icon name={config.icon} style={{ marginRight: "0.5rem" }} />
          {config.label}
        </div>
        <div style={STYLES.fieldValue(value)}>{value || "Not provided"}</div>
      </div>
    );
  };

  isFaculty = () => {
    const { data } = this.props;
    const { profileData } = this.state;

    const userRole = profileData?.role || data.metadata.role;
    const userType = profileData?.user_type || data.metadata.user_type;
    const email = profileData?.email || data.metadata.email;
    const userName =
      profileData?.name || profileData?.full_name || data.metadata.name;

    return (
      userRole === "faculty" ||
      userType === "faculty" ||
      (email && email.includes("faculty")) ||
      (userName && userName.toLowerCase().includes("faculty"))
    );
  };

  // Handle library ID update for faculty
  handleLibraryIdUpdate = async (newLibraryId) => {
    const { profileData } = this.state;

    try {
      // Call API to update the library ID
      await userApi.updateUserProfile(profileData.id, {
        library_id: newLibraryId,
      });

      // Update local state
      this.safeSetState({
        profileData: {
          ...profileData,
          library_id: newLibraryId,
        },
      });

      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  };

  renderFacultyLibrarySection = () => {
    const { profileData } = this.state;

    if (!profileData) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            minHeight: "400px",
          }}
        >
          <Message info style={{ maxWidth: "400px" }}>
            <Message.Header>Faculty Library Section Unavailable</Message.Header>
            <p>
              Profile data is required to display the faculty library section.
            </p>
          </Message>
        </div>
      );
    }

    return (
      <div style={{ padding: "1rem" }}>
        <Header as="h3" style={{ color: "#225745", marginBottom: "1.5rem" }}>
          Faculty Library Barcode
        </Header>

        {/* Faculty Name and ID */}
        <div style={{ marginBottom: "2rem", textAlign: "center" }}>
          <Header as="h4" style={{ color: "#333", marginBottom: "0.5rem" }}>
            {profileData.full_name || profileData.name}
          </Header>
        </div>

        {/* Use FacultyLibraryIdSection component in display-only mode */}
        <Grid>
          <FacultyLibraryIdSection
            user={profileData}
            onLibraryIdUpdate={this.handleLibraryIdUpdate}
            initialLibraryId={profileData.library_id}
            displayMode="barcode-only"
            showTitle={false}
            showDescription={false}
            showInput={false}
            showButtons={false}
            showUpdateButton={false}
            showClearButton={false}
            showPrintButton
          />
        </Grid>
      </div>
    );
  };

  renderLibraryCard = () => {
    const { profileData } = this.state;
    const { currentUser } = this.props;

    if (!profileData) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            minHeight: "400px",
          }}
        >
          <Message info style={{ maxWidth: "400px" }}>
            <Message.Header>Library Card Unavailable</Message.Header>
            <p>Profile data is required to display the library card.</p>
          </Message>
        </div>
      );
    }

    const userData = {
      ...profileData,
      image_url: profileData.image_url,
    };

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          minHeight: "400px",
          padding: "1rem",
        }}
      >
        <div
          style={{
            transform: "scale(0.8)",
            transformOrigin: "center",
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <LibraryCard user={userData} currentUser={currentUser} />
        </div>
      </div>
    );
  };

  shouldShowLibraryCard = () => {
    const { data } = this.props;
    const { profileData } = this.state;

    const userRole = profileData?.role || data.metadata.role;
    const userType = profileData?.user_type || data.metadata.user_type;

    // Check multiple possible role indicators
    const rolesToHide = ["librarian", "admin"];

    // Check role field
    if (rolesToHide.includes(userRole)) {
      return false;
    }

    // Check user_type field
    if (rolesToHide.includes(userType)) {
      return false;
    }

    return true;
  };

  renderUserInfo = () => {
    const { data } = this.props;
    const { profileData, profileError } = this.state;

    return (
      <div style={{ height: "100%", padding: "1.5rem" }}>
        {/* Profile Image and Basic Info - Horizontal Layout */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "2rem",
            padding: "1rem",
            backgroundColor: "transparent",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          {/* Profile Image */}
          <div style={{ marginRight: "1.5rem", flexShrink: 0 }}>
            {this.renderProfileImage("compact")}
          </div>

          {/* Name and Patron ID */}
          <div style={{ flex: 1 }}>
            <Header
              as="h3"
              style={{ margin: "0 0 0.5rem 0", color: "#225745" }}
            >
              {profileData?.full_name || data.metadata.name}
            </Header>
            <p style={{ color: "#666", fontSize: "0.9em", margin: 0 }}>
              Patron ID: {data.metadata.pid}
            </p>
            {data.metadata.email && (
              <p
                style={{
                  color: "#888",
                  fontSize: "0.85em",
                  margin: "0.25rem 0 0 0",
                }}
              >
                {data.metadata.email}
              </p>
            )}
          </div>
        </div>

        {/* Error Message */}
        {profileError && (
          <Message warning style={{ marginBottom: "1rem" }}>
            <Message.Header>Profile Data Notice</Message.Header>
            <p>{profileError}</p>
          </Message>
        )}

        {/* Contact Information */}
        <div style={{ marginBottom: "2rem" }}>
          <div
            style={{
              fontSize: "1.1em",
              fontWeight: "bold",
              color: "#f4b952",
              marginBottom: "1rem",
              borderBottom: "2px solid #f4b952",
              paddingBottom: "0.5rem",
            }}
          >
            Contact Information
          </div>
          {this.renderField("email", profileData?.email || data.metadata.email)}
          {this.renderField(
            "username",
            profileData?.username || data.metadata.username
          )}
          {this.renderField("contact_number", profileData?.contact_number)}
        </div>

        {/* Academic Information - Only show for students */}
        {this.renderAcademicInfo()}
      </div>
    );
  };

  renderAcademicInfo = () => {
    const { data } = this.props;
    const { profileData } = this.state;

    if (!profileData) return null;

    // Hide academic information for librarians, faculty, and admin
    const userRole = profileData.role || data.metadata.role;
    const userType = profileData.user_type || data.metadata.user_type;
    const email = profileData?.email || data.metadata.email;
    const userName =
      profileData?.name || profileData?.full_name || data.metadata.name;

    const rolesToHide = ["librarian", "faculty", "admin"];

    // Check multiple indicators
    if (
      rolesToHide.includes(userRole) ||
      rolesToHide.includes(userType) ||
      (email && email.includes("faculty")) ||
      (userName && userName.toLowerCase().includes("faculty"))
    ) {
      return null;
    }

    return (
      <div>
        <div
          style={{
            fontSize: "1.1em",
            fontWeight: "bold",
            color: "#f4b952",
            marginBottom: "1rem",
            borderBottom: "2px solid #f4b952",
            paddingBottom: "0.5rem",
          }}
        >
          Academic Information
        </div>
        {this.renderField("department", profileData.department)}
        {this.renderField("program", profileData.program)}
        {this.renderField("year", profileData.year)}
        {this.renderField("semester", profileData.semester)}
      </div>
    );
  };

  renderModalContent = () => {
    const { profileLoading } = this.state;

    if (profileLoading) {
      return (
        <div style={{ textAlign: "center", padding: "3rem" }}>
          <Loader active inline />
          <p style={{ marginTop: "1rem" }}>Loading profile data...</p>
        </div>
      );
    }

    const showLibraryCard = this.shouldShowLibraryCard();
    const isFaculty = this.isFaculty();

    if (!showLibraryCard && !isFaculty) {
      // For admin and librarian - show only user info
      return <div style={{ minHeight: "500px" }}>{this.renderUserInfo()}</div>;
    }

    if (isFaculty) {
      // For faculty - show user info and faculty library section
      return (
        <Grid style={{ margin: 0, minHeight: "500px" }}>
          <Grid.Row style={{ padding: 0 }}>
            {/* Left Column - User Information */}
            <Grid.Column
              width={6}
              style={{
                backgroundColor: "#f8f9fa",
                borderRight: "1px solid #dee2e6",
                padding: 0,
              }}
            >
              {this.renderUserInfo()}
            </Grid.Column>

            {/* Right Column - Faculty Library Section */}
            <Grid.Column width={10} style={{ padding: 0 }}>
              {this.renderFacultyLibrarySection()}
            </Grid.Column>
          </Grid.Row>
        </Grid>
      );
    }

    // For students - show both user info and library card
    return (
      <Grid style={{ margin: 0, minHeight: "500px" }}>
        <Grid.Row style={{ padding: 0 }}>
          {/* Left Column - User Information */}
          <Grid.Column
            width={6}
            style={{
              backgroundColor: "#f8f9fa",
              borderRight: "1px solid #dee2e6",
              padding: 0,
            }}
          >
            {this.renderUserInfo()}
          </Grid.Column>

          {/* Right Column - Library Card */}
          <Grid.Column width={10} style={{ padding: 0 }}>
            {this.renderLibraryCard()}
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  };

  render() {
    const { data } = this.props;
    const { showModal } = this.state;

    const recordInfo = (
      <>
        <label className="muted">Patron</label> {data.metadata.pid}{" "}
        <CopyButton text={data.metadata.id} />
        <br />
        <div style={{ marginTop: "0.5rem" }}>
          <button
            type="button"
            onClick={this.openModal}
            style={{
              background: "none",
              border: "none",
              color: "#f4b952",
              cursor: "pointer",
              textDecoration: "underline",
              fontSize: "0.9em",
            }}
          >
            <Icon name="eye" />
            Show Details
          </button>
        </div>
      </>
    );

    const emailBody = `${data.metadata.name},${invenioConfig.APP.EMAILS_PREFILL.footer}`;

    const titleWithImage = (
      <div style={{ display: "flex", alignItems: "center" }}>
        {this.renderProfileImage("compact")}
        <div>
          <Header.Subheader>
            <EmailLink
              email={data.metadata.email}
              subject={invenioConfig.APP.EMAILS_PREFILL.subjectPrefix}
              body={emailBody}
            />{" "}
            <CopyButton text={data.metadata.email} />
          </Header.Subheader>
          {data.metadata.name}
        </div>
      </div>
    );

    return (
      <>
        <DetailsHeader
          title={titleWithImage}
          subTitle=""
          pid={data.metadata.id}
          icon={null}
          recordInfo={recordInfo}
        />

        <Modal
          open={showModal}
          onClose={this.closeModal}
          size={
            this.shouldShowLibraryCard() || this.isFaculty() ? "large" : "small"
          }
          closeIcon
        >
          <Modal.Header>
            <Icon
              name="user"
              style={{ color: "#225745", marginRight: "1rem" }}
            />
            Patron Details - {data.metadata.name || data.metadata.pid}
          </Modal.Header>
          <Modal.Content style={{ padding: 0 }}>
            {this.renderModalContent()}
          </Modal.Content>
        </Modal>
      </>
    );
  }
}

PatronHeader.propTypes = {
  data: PropTypes.object.isRequired,
  currentUser: PropTypes.shape({
    role: PropTypes.string,
    user_type: PropTypes.string,
    type: PropTypes.string,
    roles: PropTypes.array,
    is_librarian: PropTypes.bool,
    isLibrarian: PropTypes.bool,
  }),
};
